import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  showTimeByDateType,
  newMathFormat,
  mathFormat,
} from '@/pages/intelligence/utils/commonUtils';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import { DATA_SOURCE_NAME_MAP } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { getMobileCountriesChartName } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/_utils/countryName';

const COUNTRIES_PIE_LEGEND_GAP = 20;
const COUNTRIES_TOOLTIP_CONFIG = {
  appendToBody: true,
  extraCssText: 'z-index: 9999999;',
};

const Visualis: React.FC<any> = (props: any) => {
  const {
    barSeries,
    pieSeries,
    middleTitle,
    chartType,
    imageType = 'mobile',
    dataSource,
    regionSegVal,
    lineData,
    metric,
    setChartLoading,
    chartLoading,
    setChartLineLoading,
    chartLineLoading,
    dateType,
    startDate,
    endDate,
    // getRpdTypeData,
    regionsCountries,
    isNoData,
    baseReleaseTime,
    earliestPeriod,
    pageId,
    isStackLine,
    secondTitleRight,
    firstWeekSinceReleaseDates = [],
  } = props;

  // console.log('VisualisVisualis', props);

  const { locale, formatMessage } = useIntlExtend();
  const localizedOthersName = formatMessage({ id: 'dbi_others' });

  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const barNameList = useMemo(() => {
    return barSeries?.map((item: { name: any }) => item?.name) || [];
  }, [barSeries]);

  const barSeriesData = useMemo(() => {
    return barSeries?.map((item: any, i: number): any => ({
      type: 'bar',
      name: item.name,
      value: item.value ? item.value : 0,
      cover: item.cover,
      itemStyle: {
        color: ECHARTSCOLOR[i],
      },
      label: {
        show: true,
        position: 'top',
        color: '#444',
        distance: 4,
        fontSize: 12,
        formatter: (params: any) => `${newMathFormat(Number(params.value), true)}`,
      },
    }));
  }, [barSeries]);

  const dataSourceNames = useMemo(() => {
    if (!Array.isArray(dataSource) && dataSource == '') return [];
    if (Array.isArray(dataSource)) {
      return dataSource.map((item) => DATA_SOURCE_NAME_MAP[item]).filter((item) => item);
    }
    if (dataSource != '') {
      return [DATA_SOURCE_NAME_MAP[dataSource]];
    }
  }, [dataSource]);

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData,
    setChartLoading,
    barOrPieData: barSeries,
    // titleNow,
    dataSourceEachers: dataSourceNames,
    barNameList,
    topChartsType: 'stats-countries-bar',
    cardWidth: cardRef?.current?.clientWidth,
    imageType: regionSegVal,
    ifNoBarImg: regionSegVal == 'region',
    enableAutoBarWidth: true,
    tooltipExtraConfig: COUNTRIES_TOOLTIP_CONFIG,
    chartType,
    regionSegVal,
    startDate,
    endDate,
    secondTitleRight,
    // getLocalizedCountryName,
  });

  // 获取饼图hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeries,
    pieTitle: middleTitle,
    setChartLoading: setChartLineLoading,
    chartLoading: chartLineLoading,
    chartType,
    chartsInstance,
    titleNow: metric,
    startDate,
    endDate,
    secondTitleRight,
    tooltip: COUNTRIES_TOOLTIP_CONFIG,
    pieLegendGap: COUNTRIES_PIE_LEGEND_GAP,
  });

  // 组装rpd折线图数据
  const getRpdTypeData = (list: any) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const i in list) {
      let dauData = {} as any;
      if (list[i].metrics && Array.isArray(list[i].metrics) && list[i].metrics?.length > 0) {
        if (pageId === 'activeusers') {
          dauData = list[i].metrics.find((item: any) => item.metric == 'dau');
        } else if (pageId === 'revenue') {
          dauData = list[i].metrics.find((item: any) => item.metric == 'revenue');
        } else if (pageId === 'downloads') {
          dauData = list[i].metrics.find((item: any) => item.metric == 'download');
        }
      }

      if (dauData) {
        arr.push({
          metric: dauData.metric,
          source: dataSource,
          time: showTimeByDateType(list[i].time, dateType?.toLocaleLowerCase()),
          value: dauData.value,
          predict: dauData.b_predict,
        });
      }
    }
    return arr;
  };

  const lineSeriesData = useMemo(() => {
    const arrGameData: any = [];
    for (const n in lineData) {
      const history = getRpdTypeData(lineData?.[n]?.time_line);
      const historyMap = new Map(
        history.map((historyItem: any) => [historyItem?.time, historyItem]),
      );
      const objTemp = {
        cover: lineData[n].market,
        id: lineData[n].market,
        name: getMobileCountriesChartName({
          regionsCountries,
          countryCode: lineData[n].market,
          regionSegVal,
          fallbackName: lineData[n].name,
          localizedOthersName,
        }),
        history: firstWeekSinceReleaseDates.length
          ? firstWeekSinceReleaseDates.map((date: string) => {
            return historyMap.get(date) || { time: date, source: dataSource, value: null };
          })
          : history,
      };
      arrGameData.push(objTemp);
    }

    const temp = {
      source: Array.isArray(arrGameData) ? arrGameData : [],
    };
    return temp;
  }, [lineData, regionsCountries, regionSegVal, pageId, dataSource, dateType, localizedOthersName, locale, firstWeekSinceReleaseDates]);

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric,
    topChartsType: 'stats-countries-line',
    imageType: regionSegVal,
    setChartLoading,
    chartType,
    // paLoading: countryLineLoading,
    dateType,
    baseReleaseTime: showTimeByDateType(baseReleaseTime, dateType?.toLocaleLowerCase()),
    earliestPeriod: showTimeByDateType(earliestPeriod, dateType?.toLocaleLowerCase()),
    isStackLine,
    secondTitleRight,
    shouldMoveTooltipOthersLast: true,
    tooltipOthersNames: ['others', localizedOthersName],
    tooltip: COUNTRIES_TOOLTIP_CONFIG,
  });

  return (
    <>
      <div className="card-line" ref={cardRef}>
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={
            chartType == 'line' || chartType == 'stackedLine' ? chartLineLoading : chartLoading
          }
          lineOption={lineOption}
          chartType={chartType}
          isNodata={isNoData}
          pieOption={pieOption}
          barOption={barOption}
        />
      </div>
    </>
  );
};

export default Visualis;
